import React, { useEffect } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'

import { useAppDispatch } from '@/stores/hooks.ts'
import { useLastUserId, userActions } from '@/stores/slices/user-slice.ts'
import { withNavigationPanel } from '@/layouts/dashboard/navigation-panel/navigation-panel-hoc.tsx'
import { MainContent } from '@/layouts/dashboard/main-content.tsx'
import { useMobileSelect } from '@/hooks'

import { LayoutTitle } from '../../components'
import { resetAppAction } from '../../stores'
import { useGetPersonalSettings, useGetUserInfo } from '../../services'
import { axiosInstance, logoutInterceptor } from '../../services/root.ts'
import { APP_ROUTES, CONTENT_WRAPPER_SX } from '../../constants.ts'

import { NavigationMenu } from './navigation-menu.tsx'
import { PermissionCheck } from './permission-check.tsx'

const MainContentWithNavigationPanel = withNavigationPanel(MainContent)

export const DashboardLayout = (): React.ReactElement => {
  const dispatch = useAppDispatch()
  const qc = useQueryClient()
  const lastUserId = useLastUserId()
  const { isMobile } = useMobileSelect()

  const getUserInfo = useGetUserInfo()
  const { data: { data: user } = {}, isLoading, isError, isSuccess, isFetched } = getUserInfo
  const { data: dataPersonalSettings, isSuccess: isSuccessSettings } = useGetPersonalSettings(user?.id, {
    enabled: isSuccess && !!user,
  })

  useEffect(() => {
    if (isSuccess && user) {
      if (lastUserId !== user.id) {
        dispatch(resetAppAction())
      }
      dispatch(userActions.setUser(user))
      dispatch(userActions.setLogin(''))
    }
  }, [isSuccess, lastUserId, user])

  useEffect(() => {
    const settings = dataPersonalSettings?.data

    if (isSuccessSettings && settings) {
      dispatch(userActions.setPersonalSettings(settings))
    }
  }, [isSuccessSettings, dataPersonalSettings])

  useEffect(() => {
    const interceptor = logoutInterceptor(dispatch)
    return () => {
      qc.removeQueries()
      axiosInstance.interceptors.response.eject(interceptor)
    }
  }, [])

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'absolute',
          backgroundColor: 'background.default',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
        }}
      >
        <CircularProgress size={50} />
      </Box>
    )
  }

  if ((isFetched && !user?.id) || isError) {
    return <Navigate to={APP_ROUTES.Login} replace />
  }

  return isMobile ? (
    <>
      <NavigationMenu />
      <Box sx={CONTENT_WRAPPER_SX}>
        <LayoutTitle />
        <Box minHeight="50vh">
          <PermissionCheck />
        </Box>
      </Box>
    </>
  ) : (
    <MainContentWithNavigationPanel />
  )
}
